// SPDX-License-Identifier: AGPL-3.0-or-later

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { Toaster } from '@intakeform/ui';
import { usePageTransition } from './hooks/usePageTransition.js';

interface BoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Browser devtools only: the web app is a static SPA behind nginx, so this
    // never reaches the server log stream (context#129's no-console rule targets that).
    // eslint-disable-next-line no-console
    console.error('error boundary:', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="app-main">
          <div className="error-banner">
            <strong>Something went wrong.</strong>
            <p className="muted">{this.state.error.message}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppOutlet() {
  usePageTransition();
  return <Outlet />;
}

export function App() {
  return (
    <ErrorBoundary>
      <Toaster>
        <AppOutlet />
      </Toaster>
    </ErrorBoundary>
  );
}
